import React from "react";
import BrochureWithText from "../../Brochure/BrochureWithText";
import { errorMessages } from "../../../../constants/errorMessages.constants";
import { typeOfPAF } from "../../../../constants/typeOfPAF";
import {
  getAge,
  isDispensingMoreThan3DaysBetween2Dates,
} from "../../../../shared/helpers";
import {
  checkIfNumberOfCyclesExceeded,
  checkIfNumberOfWeeksExceeded,
  checkIfTotalSupplyWeeksExceeded,
} from "./pafRenewScheduleChecks";

function isNotSelectedAnyCapsulePerCycle(form) {
  if (form?.capsules?.length > 0) {
    return form?.capsules?.some(
      (capsule) => Boolean(capsule.label) || Boolean(capsule.other)
    );
  }
  return false;
}

/**
 * First branch group in `nextPage`: schedule / limits / pregnancy gates before compliance block.
 * Returns a patch for `setErrorMessage` or null if no modal.
 */
export function evaluatePafRenewLimitModals({
  form,
  typeOfForm,
  displayWeeks,
  hcp,
}) {
  if (
    checkIfNumberOfCyclesExceeded(form, typeOfForm) &&
    form.continueWithMaximumNumberOfCyclesExceeded === false &&
    !displayWeeks
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: errorMessages.MAXIM_NUMBER_OF_CYLES_EXCEEDED_TITLE,
      description:
        typeOfForm === typeOfPAF.MALE ? (
          errorMessages.MAXIM_NUMBER_OF_CYLES_EXCEEDED_DESCRIPTION_MALE
        ) : typeOfForm === typeOfPAF.WOMANOFNCP ? (
          errorMessages.MAXIM_NUMBER_OF_CYLES_EXCEEDED_DESCRIPTION_WOMANOFNCP
        ) : (
          <BrochureWithText type="CYCLES_EXCEEDED_WCBP" hcp={hcp} />
        ),
      buttonText: "Continue",
    };
  }

  if (
    checkIfNumberOfWeeksExceeded(form, typeOfForm) &&
    form.continueWithMaximumNumberOfWeeksExceeded === false &&
    displayWeeks
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: "Maximum number of weeks exceeded",
      description: (
        <BrochureWithText
          type="WEEKS_EXCEEDED"
          typeOfPAF={typeOfPAF}
          registerPatientType={typeOfForm}
          maxCapsulesOrWeeks={form}
          hcp={hcp}
        />
      ),
      buttonText: "Continue",
    };
  }

  if (
    checkIfTotalSupplyWeeksExceeded(form, typeOfForm) &&
    form.continueWithMaximumNumberOfWeeksExceeded === false &&
    form?.schedule_display_type === "weeks_per_cycle"
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: "Maximum number of weeks exceeded",
      description: (
        <BrochureWithText
          type="WEEKS_EXCEEDED"
          typeOfPAF={typeOfPAF}
          registerPatientType={typeOfForm}
          maxCapsulesOrWeeks={form}
          hcp={hcp}
        />
      ),
      buttonText: "Continue",
    };
  }

  if (
    checkIfNumberOfWeeksExceeded(form, typeOfForm) &&
    form.continueWithMaximumNumberOfWeeksExceededAgain === false &&
    displayWeeks
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: "Capsules outside permitted Quantity",
      description: (
        <BrochureWithText
          type="CAPSULES_EXCEEDED"
          typeOfPAF={typeOfPAF}
          registerPatientType={typeOfForm}
          maxCapsulesOrWeeks={form}
          hcp={hcp}
        />
      ),
      buttonText: "Continue",
    };
  }

  if (
    displayWeeks &&
    form.continueMaximumNumberOfCapsulesExceeded !== true &&
    (typeOfPAF.WOMANOFCP === typeOfForm
      ? !!form.max_capsules_wcbp
      : !!form.max_capsules_m_or_wncbp) &&
    form.total_capsules >
      (typeOfPAF.WOMANOFCP === typeOfForm
        ? form.max_capsules_wcbp
        : form.max_capsules_m_or_wncbp)
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: "Capsules outside permitted Quantity",
      description: (
        <BrochureWithText
          type="CAPSULES_EXCEEDED"
          typeOfPAF={typeOfPAF}
          registerPatientType={typeOfForm}
          maxCapsulesOrWeeks={form}
          hcp={hcp}
        />
      ),
      buttonText: "Continue",
    };
  }

  if (
    getAge(form.patient_dob) < 18 &&
    form.continueWithDateOfBirthUnderEighteen !== true
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: errorMessages.OUTDATED_DATE_BIRTH_TITLE,
      description: <BrochureWithText type="NEED_VALID_DATE_BIRTH" hcp={hcp} />,
      buttonText: "Continue",
    };
  }

  if (
    typeOfForm === typeOfPAF.WOMANOFCPRENEW2 &&
    form.continueWithPregnancyTestDateIncomplete !== true &&
    form.pregnancy_test === undefined
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: errorMessages.PREGNANACY_TEST_DATE_INCOMPLETE_TITLE,
      description: (
        <BrochureWithText
          type="NEED_VALID_NEGATIVE_TEST_INCOMPLETE"
          hcp={hcp}
        />
      ),
      buttonText: "Continue",
    };
  }

  if (
    typeOfForm === typeOfPAF.WOMANOFCPRENEW2 &&
    isDispensingMoreThan3DaysBetween2Dates(new Date(), form.pregnancy_test) &&
    form.continueWithPregnancyTestDateOutdated === false
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: errorMessages.OUTDATED_PREGANACY_TEST_TITLE,
      description: <BrochureWithText type="NEED_VALID_NEGATIVE_TEST" hcp={hcp} />,
      buttonText: "Continue",
    };
  }

  return null;
}

/**
 * Compliance / indication / checkbox gates after limit modals pass.
 */
export function evaluatePafRenewComplianceModals({
  form,
  typeOfForm,
  displayWeeks,
  hcp,
  confirmarmation_risk_prescriber_local,
}) {
  if (
    confirmarmation_risk_prescriber_local !== true &&
    form.continueWithoutConfirmation !== true &&
    typeOfForm !== typeOfPAF.WOMANOFNCP &&
    Boolean(form.prescription_confirmation_risks.length)
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: errorMessages.CONFIRMATION_NOT_PROVIDED_TITLE,
      description: (
        <BrochureWithText
          type="CONFIRMATION_NOT_PROVIDED_DESCRIPTION"
          hcp={hcp}
          confirmationRiskLength={form?.prescription_confirmation_risks?.length}
        />
      ),
      buttonText: "Continue",
    };
  }

  if (form?.indication?.is_indication_other && form?.continueOffLabel === false) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: "Continue with off-label use",
      description: `You have selected an indication that is not officially approved for use with ${hcp?.name}. Are you sure you want to proceed?`,
      buttonText: "Continue",
    };
  }

  if (
    form?.continueIndicationIncomplete !== true &&
    form?.indication.label === "Select"
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: errorMessages.INDICATION_INCOMPLETE_TITLE,
      description: (
        <BrochureWithText type="CONTINUE_INDICATION_INCOMPLETE" hcp={hcp} />
      ),
      buttonText: "Continue",
    };
  }

  if (
    form?.continueCapsulePerCycleIncomplete !== true &&
    !isNotSelectedAnyCapsulePerCycle(form)
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: displayWeeks
        ? errorMessages.CAPSULES_PER_WEEKS_INCOMPLETE
        : errorMessages.CAPSULES_PER_CYCLE_INCOMPLETE,
      description: (
        <BrochureWithText
          type="CONTINUE_CAPSULE_PER_CYCLE_INCOMPLETE"
          hcp={hcp}
          scheduleDisplayType={form?.schedule_display_type}
        />
      ),
      buttonText: "Continue",
    };
  }

  if (
    form?.continueNumberOfCyclesIncomplete !== true &&
    (!displayWeeks || form?.schedule_display_type === "weeks_per_cycle") &&
    (form?.number_cycles?.value === 0 ||
      form?.number_cycles?.label === "None" ||
      form?.number_cycles === null ||
      !form?.number_cycles)
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: errorMessages.NUMBER_OF_CYCLES_INCOMPLETE,
      description: (
        <BrochureWithText
          type="CONTINUE_NUMBER_OF_CYCLES_INCOMPLETE"
          hcp={hcp}
        />
      ),
      buttonText: "Continue",
    };
  }

  if (
    form?.schedule_display_type !== "weeks_per_cycle" &&
    form?.continueNumberOfWeeksIncomplete !== true &&
    displayWeeks &&
    (form?.number_weeks === null ||
      form?.number_weeks?.label === "None" ||
      !form?.number_weeks)
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: errorMessages.NUMBER_OF_WEEKS_INCOMPLETE,
      description: (
        <BrochureWithText
          type="CONTINUE_NUMBER_OF_WEEKS_INCOMPLETE"
          hcp={hcp}
        />
      ),
      buttonText: "Continue",
    };
  }

  if (
    form?.schedule_display_type === "weeks_per_cycle" &&
    form?.continueNumberOfWeeksIncomplete !== true &&
    (form?.weeks_per_cycle?.id === null ||
      form?.weeks_per_cycle?.label === "None" ||
      !form?.weeks_per_cycle)
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: errorMessages.NUMBER_OF_WEEKS_INCOMPLETE,
      description: (
        <BrochureWithText
          type="CONTINUE_NUMBER_OF_WEEKS_INCOMPLETE"
          hcp={hcp}
        />
      ),
      buttonText: "Continue",
    };
  }

  if (
    form.confirmation_risk_patient !== true &&
    form.continueWithoutConfirmation !== true &&
    typeOfForm !== typeOfPAF.WOMANOFNCP &&
    Boolean(form.prescription_confirmation_risks.length)
  ) {
    return {
      type: "error",
      modalState: 1,
      hasSecondState: false,
      title: errorMessages.CONFIRMATION_NOT_PROVIDED_TITLE,
      description: (
        <BrochureWithText
          type="CONFIRMATION_NOT_PROVIDED_DESCRIPTION"
          hcp={hcp}
          confirmationRiskLength={form?.prescription_confirmation_risks?.length}
        />
      ),
      buttonText: "Continue",
    };
  }

  return null;
}
